import { useMemo } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import t from "@/lang/lang";
import {
  GfsOffer,
  GfsTargetOrganismSelection,
} from "@/services/backend/gfs/offer/types";
import {
  usePestcontrolPestGroupListQuery,
  usePestcontrolPestListQuery,
} from "@/services/backend/pestcontrol/api/pest-api";
import {
  Pest,
  PestGroup,
  PestKind,
} from "@/services/backend/pestcontrol/types/pest";
import {
  groupToSnapshot,
  pestToSnapshot,
} from "@/services/backend/pestcontrol/lib/monitor";
import { GfsOfferHtmlEditor } from "@/services/backend/gfs/offer/components/offer-html-editor";
import { Button } from "@/shared/components/ui/button";
import { InputDebounced } from "@/shared/components/inputs/input-debounced";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shared/components/ui/command";
import { Label } from "@/shared/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { cn } from "@/shared/lib/utils";

type SelectionOption<TSnapshot> = {
  key: string;
  label: string;
  snapshot: TSnapshot;
};

export function GfsTargetOrganismSelectionEditor({
  offer,
  value,
  offerTitle,
  disabled,
  onChange,
  onOfferTitleChange,
}: {
  offer: GfsOffer;
  value: GfsTargetOrganismSelection;
  offerTitle: string;
  disabled: boolean;
  onChange: (selection: GfsTargetOrganismSelection, labels: string[]) => void;
  onOfferTitleChange: (value: string) => void;
}) {
  const { data: groupsData } = usePestcontrolPestGroupListQuery({
    company: { active: true, values: [offer.companyId] },
    limit: 500,
    offset: 0,
  });
  const { data: pestsData } = usePestcontrolPestListQuery({
    company: { active: true, values: [offer.companyId] },
    limit: 500,
    offset: 0,
  });

  const effectiveSelection = useMemo(
    () => effectiveGfsTargetOrganismSelection(offer, value),
    [offer, value],
  );
  const options = useMemo(
    () =>
      buildOptions(
        groupsData?.items ?? [],
        pestsData?.items ?? [],
        effectiveSelection,
      ),
    [effectiveSelection, groupsData?.items, pestsData?.items],
  );

  const emitChange = (next: GfsTargetOrganismSelection) => {
    const normalized = normalizeSelection(next, emptySelection());
    onChange(normalized, targetOrganismLabels(normalized));
  };

  return (
    <div className="rounded-md border p-4">
      <div className="mb-3 space-y-2">
        <div className="text-sm font-semibold">{t("Zielorganismen")}</div>
        <GfsOfferHtmlEditor
          value={offerTitle}
          disabled={disabled}
          onChange={onOfferTitleChange}
          minHeightClassName="min-h-20"
        />
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <TargetOrganismMultiSelect
          title={t("Schädlingsgruppen")}
          options={options.groupOptions}
          selectedKeys={effectiveSelection.pestGroups.map(
            (group) => group.sourceId,
          )}
          disabled={disabled}
          onChange={(pestGroups) =>
            emitChange({ ...effectiveSelection, pestGroups })
          }
        />
        <TargetOrganismMultiSelect
          title={t("Schädlingskategorien")}
          options={options.categoryOptions}
          selectedKeys={effectiveSelection.pestCategories.map(
            (pest) => pest.sourceId,
          )}
          disabled={disabled}
          onChange={(pestCategories) =>
            emitChange({ ...effectiveSelection, pestCategories })
          }
        />
        <TargetOrganismMultiSelect
          title={t("Indikatoren")}
          options={options.indicatorOptions}
          selectedKeys={effectiveSelection.pestIndicators.map(
            (pest) => pest.sourceId,
          )}
          disabled={disabled}
          onChange={(pestIndicators) =>
            emitChange({ ...effectiveSelection, pestIndicators })
          }
        />
      </div>
    </div>
  );
}

export function GfsOfferSubjectEditor({
  subject,
  disabled,
  onChange,
}: {
  subject: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <div className="rounded-md border p-4">
      <div className="space-y-2">
        <Label htmlFor="gfs-offer-letter-subject">
          {t("Betreff im Anschreiben")}
        </Label>
        <InputDebounced
          id="gfs-offer-letter-subject"
          value={subject}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
        />
        <p className="text-muted-foreground text-xs">
          {t(
            "Dieser Betreff wird im Anschreiben und im Angebotskopf verwendet.",
          )}
        </p>
      </div>
    </div>
  );
}

export function effectiveGfsTargetOrganismSelection(
  offer: GfsOffer,
  value: GfsTargetOrganismSelection,
): GfsTargetOrganismSelection {
  return normalizeSelection(value, deriveSelectionFromOffer(offer));
}

function TargetOrganismMultiSelect<TSnapshot>({
  title,
  options,
  selectedKeys,
  disabled,
  onChange,
}: {
  title: string;
  options: SelectionOption<TSnapshot>[];
  selectedKeys: string[];
  disabled: boolean;
  onChange: (next: TSnapshot[]) => void;
}) {
  const selectedSet = useMemo(() => new Set(selectedKeys), [selectedKeys]);
  const buttonLabel = compactSelectionLabel(options, selectedKeys);

  return (
    <div className="grid content-start gap-1.5">
      <Label className="min-h-5 text-sm">{title}</Label>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            disabled={disabled}
            className="w-full justify-between font-normal"
          >
            <span className="truncate">{buttonLabel}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[420px] p-0" align="start">
          <div className="flex items-center justify-end border-b p-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={disabled || selectedKeys.length === 0}
              onClick={() => onChange([])}
            >
              {t("Alle abwählen")}
            </Button>
          </div>
          <Command>
            <CommandInput placeholder={t("Suchen")} />
            <CommandList>
              <CommandEmpty>{t("Keine Treffer")}</CommandEmpty>
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option.key}
                    value={`${option.key} ${option.label}`}
                    onSelect={() => {
                      const nextKeys = selectedSet.has(option.key)
                        ? selectedKeys.filter((key) => key !== option.key)
                        : [...selectedKeys, option.key];
                      const nextSet = new Set(nextKeys);
                      onChange(
                        options
                          .filter((candidate) => nextSet.has(candidate.key))
                          .map((candidate) => candidate.snapshot),
                      );
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        selectedSet.has(option.key)
                          ? "opacity-100"
                          : "opacity-0",
                      )}
                    />
                    <span>{option.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function buildOptions(
  groups: PestGroup[],
  pests: Pest[],
  selection: GfsTargetOrganismSelection,
) {
  return {
    groupOptions: sortedOptions(
      groups.map(groupToSnapshot),
      selection.pestGroups,
      (group) => group.sourceId,
      (group) => group.name,
    ),
    categoryOptions: sortedOptions(
      pests
        .filter((pest) => pest.kind === PestKind.Category)
        .map(pestToSnapshot),
      selection.pestCategories,
      (pest) => pest.sourceId,
      (pest) => pest.name,
    ),
    indicatorOptions: sortedOptions(
      pests
        .filter((pest) => pest.kind === PestKind.Indicator)
        .map(pestToSnapshot),
      selection.pestIndicators,
      (pest) => pest.sourceId,
      (pest) => pest.name,
    ),
  };
}

function sortedOptions<TSnapshot extends { sourceId: string; name?: string }>(
  options: TSnapshot[],
  selected: TSnapshot[],
  keyOf: (snapshot: TSnapshot) => string,
  labelOf: (snapshot: TSnapshot) => string,
): SelectionOption<TSnapshot>[] {
  const byKey = new Map<string, SelectionOption<TSnapshot>>();
  [...options, ...selected].forEach((snapshot) => {
    const key = keyOf(snapshot);
    if (!key || byKey.has(key)) {
      return;
    }
    byKey.set(key, { key, label: labelOf(snapshot), snapshot });
  });
  return [...byKey.values()].sort((left, right) =>
    left.label.localeCompare(right.label, "de"),
  );
}

function compactSelectionLabel<TSnapshot>(
  options: SelectionOption<TSnapshot>[],
  selectedKeys: string[],
): string {
  if (selectedKeys.length === 0) {
    return t("Keine Auswahl");
  }
  const labelsByKey = new Map(
    options.map((option) => [option.key, option.label]),
  );
  const labels = selectedKeys
    .map((key) => labelsByKey.get(key) ?? key)
    .filter((label) => label.trim().length > 0);
  if (labels.length === 0) {
    return t("Keine Auswahl");
  }
  if (labels.length <= 2) {
    return labels.join(", ");
  }
  return `${labels.length} ${t("ausgewählt")}`;
}

function normalizeSelection(
  value: GfsTargetOrganismSelection | undefined,
  fallback: GfsTargetOrganismSelection,
): GfsTargetOrganismSelection {
  const current = value ?? emptySelection();
  if (
    current.pestGroups?.length ||
    current.pestCategories?.length ||
    current.pestIndicators?.length
  ) {
    return {
      pestGroups: uniqueBySourceID(current.pestGroups ?? []),
      pestCategories: uniqueBySourceID(current.pestCategories ?? []),
      pestIndicators: uniqueBySourceID(current.pestIndicators ?? []),
    };
  }
  return fallback;
}

function deriveSelectionFromOffer(offer: GfsOffer): GfsTargetOrganismSelection {
  const assessment = offer.calculation.assessment;
  const categories = [...(assessment.pestCategories ?? [])];
  (assessment.areaAssessments ?? []).forEach((areaAssessment) => {
    categories.push(...(areaAssessment.pestCategories ?? []));
  });

  return {
    pestGroups: uniqueBySourceID(assessment.pestGroups ?? []),
    pestCategories: uniqueBySourceID(
      categories.filter((pest) => pest.kind === PestKind.Category),
    ),
    pestIndicators: uniqueBySourceID(
      categories.filter((pest) => pest.kind === PestKind.Indicator),
    ),
  };
}

function targetOrganismLabels(selection: GfsTargetOrganismSelection): string[] {
  const labels: string[] = [];
  const seen = new Set<string>();
  const add = (label: string) => {
    const normalized = label.trim().toLowerCase();
    if (!normalized || seen.has(normalized)) {
      return;
    }
    seen.add(normalized);
    labels.push(label.trim());
  };
  selection.pestGroups.forEach((group) => add(group.name));
  selection.pestCategories.forEach((pest) => add(pest.name));
  selection.pestIndicators.forEach((pest) => add(pest.name));
  return labels;
}

function uniqueBySourceID<
  TSnapshot extends { sourceId: string; name?: string },
>(snapshots: TSnapshot[]): TSnapshot[] {
  const bySourceID = new Map<string, TSnapshot>();
  snapshots.forEach((snapshot) => {
    if (!snapshot.sourceId || bySourceID.has(snapshot.sourceId)) {
      return;
    }
    bySourceID.set(snapshot.sourceId, snapshot);
  });
  return [...bySourceID.values()].sort((left, right) =>
    displayName(left).localeCompare(displayName(right), "de"),
  );
}

function displayName(snapshot: { name?: string }) {
  return snapshot.name ?? "";
}

function emptySelection(): GfsTargetOrganismSelection {
  return {
    pestGroups: [],
    pestCategories: [],
    pestIndicators: [],
  };
}
